import React from 'react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import isPropValid from '@emotion/is-prop-valid';
import { SerializedStyles } from '@emotion/utils';
import CircularProgress from '@material-ui/core/CircularProgress';
import css from '@emotion/css';
import { PalleteColorType, mainRed } from '../../../libs/pallete';

interface InnerButtonProps {
  palleteColor: PalleteColorType;
  borderRadius?: string;
  width?: string;
  height?: string;
  isDisabled?: boolean;
  fontSize?: string;
}

interface ButtonProps extends InnerButtonProps {
  text: string;
  onClick?: (event?: React.MouseEvent<HTMLButtonElement>) => void;
  borderRadius?: string;
  className?: string;
  href?: string;
  prefixIcon?: React.ReactNode;
  postfixIcon?: React.ReactNode;
  targetBlank?: boolean;
  callback?: () => void;
  textClassName?: SerializedStyles;
  type?: 'button' | 'submit';
  loading?: boolean;
}
/**
 * render 3 types of elements based on props
 * if `href` -> react-router Link
 * if `href + targetBlank` -> normal html <a>
 * if `onClick` -> normal html <button>
 */
const PalleteButton = (props: ButtonProps) => {
  const { t } = useTranslation();
  const {
    text,
    width,
    height,
    palleteColor,
    borderRadius,
    onClick,
    className,
    isDisabled,
    href,
    fontSize,
    prefixIcon,
    postfixIcon,
    targetBlank,
    callback,
    textClassName,
    type = 'button',
    loading
  } = props;
  const translatedText = t(`Button.${text}`);
  const baseButtonProps = {
    palleteColor,
    width,
    height,
    borderRadius,
    isDisabled,
    fontSize,
    className
  };

  const renderButtonContent = () => (
    <ButtonTextWrapper>
      {loading ? (
        <CircularProgress
          size={18}
          thickness={2.5}
          css={css`
            color: #fff;
            margin-right: 8px;
          `}
        />
      ) : (
        prefixIcon
      )}
      <ButtonText css={textClassName}>
        {translatedText}
        {postfixIcon}
      </ButtonText>
    </ButtonTextWrapper>
  );

  const renderLinkButton = () =>
    targetBlank ? (
      <ExternalLinkButton
        {...baseButtonProps}
        href={href}
        target="_blank"
        onClick={callback}
        rel="noopener noreferrer"
        type={type}
      >
        {renderButtonContent()}
      </ExternalLinkButton>
    ) : (
      <RouterLinkButton {...baseButtonProps} to={href as string} onClick={callback} type={type}>
        {renderButtonContent()}
      </RouterLinkButton>
    );

  const renderNormalButton = () => (
    <StyledButton {...baseButtonProps} onClick={onClick} type={type}>
      <ButtonTextWrapper>
        {loading ? (
          <CircularProgress
            size={18}
            thickness={2.5}
            css={css`
              color: #fff;
              margin-right: 8px;
            `}
          />
        ) : (
          prefixIcon
        )}
        <ButtonText css={textClassName}>
          {translatedText}
          {postfixIcon}
        </ButtonText>
      </ButtonTextWrapper>
    </StyledButton>
  );

  return (
    <>
      {href && renderLinkButton()}
      {onClick && renderNormalButton()}
    </>
  );
};

const StyledButton = styled.button<InnerButtonProps>`
  ${({ palleteColor, borderRadius, width, height, isDisabled, fontSize }) => `
    cursor: pointer;
    background: ${palleteColor?.normal || mainRed.normal};
    color: ${palleteColor?.textColor || '#fff'};
    border: 1px solid ${palleteColor.borderColor || 'transparent'};
    border-radius: ${borderRadius || '20px'};
    width: ${width || '100%'};
    line-height: calc(${height || '40px'} - 2px);
    height: ${height || '40px'};
    ${isDisabled ? 'background-color: #dee5ec; color: #fff; pointer-events: none' : ''};
    text-align: center;
    box-sizing: border-box;
    text-transform: uppercase;
    box-sizing: border-box;
    ${fontSize && `font-size: ${fontSize}`};

    &:hover {
      background: ${palleteColor?.hover};
      border-color: ${palleteColor?.hoverBorder || 'none'};
    }
  `};
`;

const RouterLinkButton = styled(StyledButton.withComponent(Link), { shouldForwardProp: prop => isPropValid(prop) })``;
const ExternalLinkButton = styled(StyledButton.withComponent('a'))``;

const ButtonTextWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;

  /* for positioning pseudo elements from textClassName */
  position: relative;
`;

const ButtonText = styled.div`
  display: flex;
  align-items: center;
  width: max-content;
  padding: 0 10px;
  font-size: 12px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  letter-spacing: 0.5px;
`;

export default PalleteButton;
